import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from './useAuthStore';
import { toast } from 'sonner';

const SESSION_CHECK_INTERVAL = 60000; // Check every 60 seconds
const SESSION_WARNING_MINUTES = 5; // Warn 5 minutes before expiry

interface SessionState {
  isValid: boolean;
  expiresIn: number | null; // seconds until expiry
  showWarning: boolean;
}

export function useSessionTimeout() {
  const { user } = useAuthStore();
  const [sessionState, setSessionState] = useState<SessionState>({
    isValid: true,
    expiresIn: null,
    showWarning: false,
  });

  useEffect(() => {
    if (!user) {
      setSessionState({ isValid: true, expiresIn: null, showWarning: false });
      return;
    }

    let timeoutWarningShown = false;

    const checkSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error || !session) {
          setSessionState({ isValid: false, expiresIn: null, showWarning: false });

          if (!timeoutWarningShown) {
            toast.error('Your session has expired. Please sign in again.', {
              duration: 10000,
            });
            timeoutWarningShown = true;
          }

          // Redirect to auth page
          setTimeout(() => {
            window.location.href = '/auth';
          }, 2000);

          return;
        }

        const expiresAt = session.expires_at;
        if (!expiresAt) return;

        const now = Math.floor(Date.now() / 1000);
        const expiresIn = expiresAt - now;
        const expiresInMinutes = expiresIn / 60;

        // Show warning if less than 5 minutes remaining
        const shouldShowWarning =
          expiresInMinutes <= SESSION_WARNING_MINUTES && expiresInMinutes > 0;

        setSessionState({
          isValid: expiresIn > 0,
          expiresIn,
          showWarning: shouldShowWarning,
        });

        // Show one-time warning
        if (shouldShowWarning && !timeoutWarningShown) {
          toast.warning(
            `Your session will expire in ${Math.ceil(expiresInMinutes)} minutes. Any unsaved changes may be lost.`,
            {
              duration: 10000,
              action: {
                label: 'Refresh Session',
                onClick: async () => {
                  await supabase.auth.refreshSession();
                  toast.success('Session refreshed successfully');
                  timeoutWarningShown = false;
                },
              },
            }
          );
          timeoutWarningShown = true;
        }

        // Session expired
        if (expiresIn <= 0) {
          setSessionState({ isValid: false, expiresIn: 0, showWarning: false });

          toast.error('Your session has expired. Please sign in again.', {
            duration: 10000,
          });

          setTimeout(() => {
            window.location.href = '/auth';
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    };

    // Initial check
    checkSession();

    // Set up interval
    const intervalId = setInterval(checkSession, SESSION_CHECK_INTERVAL);

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        checkSession();
      }
    });

    return () => {
      clearInterval(intervalId);
      subscription.unsubscribe();
    };
  }, [user]);

  return sessionState;
}
